import React, { useMemo, useState } from "react";
import { formatAddress } from "../../utils/addressFormatter";
import type { Community } from "../../types";

const BNL_ID = "9lSkwTx49zGTNRHlOOHb";
const BLOCKS = ["A", "B", "C", "D", "E", "F", "G", "H", "I"];

/** Minimal BNL shape when Firestore community doc is unavailable */
export const BNL_FALLBACK: Community = {
  id: BNL_ID,
  name: "Bren Northern Lights",
  shortId: "BNL",
  area: "Whitefield",
  city: "Bengaluru",
  state: "Karnataka",
  pincode: "560066",
  level1: { id: "block", label: "Block", values: BLOCKS },
  level2: {
    id: "floor",
    label: "Floor",
    values: ["G", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"],
  },
  level3: {
    id: "flat",
    label: "Flat",
    prefixLevel1: true,
    digitsCount: 2,
  },
};

export type CommunityAddressResult = {
  formattedAddress: string;
  homeAddressMeta: {
    mode: "community";
    communityId: string;
    communityName: string;
    block: string;
    flat: string;
    floor?: string;
    level1Value: string;
    level2Value: string;
    level3Value: string;
    location?: { lat: number; lng: number };
    rawAddressFromMap?: string;
  };
};

type Props = {
  open: boolean;
  community?: Community | null;
  location?: { lat: number; lng: number } | null;
  rawAddress?: string;
  onConfirm: (result: CommunityAddressResult) => void;
  onSkip: () => void;
  onClose: () => void;
};

/**
 * Lightweight mobile-friendly picker for Bren Northern Lights block + flat.
 */
export const CommunityAddressSheet: React.FC<Props> = ({
  open,
  community,
  location,
  rawAddress,
  onConfirm,
  onSkip,
  onClose,
}) => {
  const c = community?.id === BNL_ID || !community ? { ...BNL_FALLBACK, ...(community || {}) } : community;
  const blocks = c.level1?.values?.length ? c.level1.values : BLOCKS;
  const [block, setBlock] = useState(blocks[0] || "A");
  const [flat, setFlat] = useState("");

  /** BNL-style flats encode floor: 905 → floor 9, 1203 → floor 12 */
  const deriveFloor = (digits: string): string | undefined => {
    if (digits.length >= 3) return String(parseInt(digits.slice(0, -2), 10));
    if (digits.length == 2) return undefined;
    return undefined;
  };

  const preview = useMemo(() => {
    try {
      let b = block;
      let f = flat || "905";
      const prefixed = f.trim().match(/^([A-Ia-i])\s*-?\s*(\d+)$/);
      if (prefixed) {
        b = prefixed[1].toUpperCase();
        f = prefixed[2];
      }
      const digits = f.replace(/\D/g, "");
      const fl = deriveFloor(digits) || "";
      return formatAddress(c as Community, b, fl, f).fullAddress;
    } catch {
      return `${block}-${flat || "??"}, ${c.name}`;
    }
  }, [c, block, flat]);

  if (!open) return null;

  const handleConfirm = () => {
    let raw = (flat || "").trim();
    let blockToUse = block;

    // Accept "C-905" / "c905" in the flat field and sync block
    const prefixed = raw.match(/^([A-Ia-i])\s*-?\s*(\d+)$/);
    if (prefixed) {
      blockToUse = prefixed[1].toUpperCase();
      raw = prefixed[2];
      setBlock(blockToUse);
    }

    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      alert("Enter your flat number (e.g. 905)");
      return;
    }
    const floor = deriveFloor(digits);
    const formatted = formatAddress(c as Community, blockToUse, floor || "", digits);
    onConfirm({
      formattedAddress: formatted.fullAddress,
      homeAddressMeta: {
        mode: "community",
        communityId: c.id || BNL_ID,
        communityName: c.name || "Bren Northern Lights",
        block: blockToUse,
        flat: formatted.level3Value,
        floor,
        level1Value: formatted.level1Value,
        level2Value: floor || "",
        level3Value: formatted.level3Value,
        location: location || undefined,
        rawAddressFromMap: rawAddress,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-5 pb-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Bren Northern Lights</h3>
            <p className="text-sm text-gray-500">Pick your block and flat (e.g. C-905). Floor is taken from the flat number.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none px-2">
            ✕
          </button>
        </div>

        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Block</label>
        <div className="grid grid-cols-5 gap-2 mb-4">
          {blocks.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBlock(b)}
              className={`py-2 rounded-lg border text-sm font-bold ${
                block === b
                  ? "bg-orange-600 text-white border-orange-600"
                  : "bg-white text-gray-700 border-gray-200"
              }`}
            >
              {b}
            </button>
          ))}
        </div>

        <label className="block text-xs font-bold uppercase text-gray-500 mb-1">Flat number</label>
        <input
          type="text"
          inputMode="numeric"
          placeholder="e.g. 905 or C-905"
          value={flat}
          onChange={(e) => setFlat(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-3 mb-3 text-lg"
        />

        <p className="text-xs text-gray-500 mb-1">Use the full flat number (905), not just 05.</p>
        <p className="text-xs text-gray-500 mb-4">Preview: <span className="font-medium text-gray-800">{preview}</span></p>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleConfirm}
            className="w-full py-3 rounded-xl bg-orange-600 text-white font-bold"
          >
            Save flat address
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="w-full py-2 text-sm text-gray-500 hover:text-gray-800"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunityAddressSheet;
